import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import cc from 'classcat'
import loadable from '@loadable/component'

import type { IconPluginData } from './IconSettings'
import { emptyIconPluginData } from '../../../../editorSidebarBlockTemplates'
import { useTranslate } from '../../../../../utils/translate'

const SettingsLayer = loadable(() => import(/* webpackChunkName: "editor" */ '../../SettingsLayer'))
const SettingsForm = loadable(() => import(/* webpackChunkName: "editor" */ './IconSettings'))

export default function IconPlugin({
  data: immutableData,
  editorMode,
  editorView,
  onEdit,
  onCancel,
  onDataChange,
  onSave,
}: Readonly<WorkspacePluginProps>): ReactElement | null {
  const data: IconPluginData = immutableData.toJS()
  const [isActive, setIsActive] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const t = useTranslate('interface')

  const pluginActiveClasses = cc([
    {
      'dali-grid-element-highlighted': isActive,
    },
  ])

  if (editorView) {
    return (
      <div className={pluginActiveClasses} ref={ref}>
        {data.type ? (
          renderIcon()
        ) : (
          <div className="dali-grid-element-placeholder">
            <span className="dali-plugin-icon-placeholder" />
            <button className="dali-grid-element-placeholder-button" onClick={() => onEdit()}>
              {t('components.iconElementComponent.addIconButton.label')}
            </button>
          </div>
        )}
        <SettingsLayer
          open={editorMode === 'edit'}
          virtualRef={ref}
          side="right"
          onEscapeKeyDown={onCancel}
          className="dali-plugin-icon-settingslayer"
          onActiveStateChange={setIsActive}
        >
          {({ renderLayout }) => (
            <SettingsForm
              data={data}
              onDataChange={onDataChange}
              onSave={onSave}
              onCancel={onCancel}
              renderLayout={renderLayout}
            />
          )}
        </SettingsLayer>
      </div>
    )
  }

  if (!data.type) return null

  return renderIcon()

  function renderIcon() {
    return (
      <div
        className={cc([
          'dali-plugin-icon',
          {
            'no-bg-or-border-styles':
              data.backgroundColor === emptyIconPluginData.backgroundColor &&
              data.backgroundSize === emptyIconPluginData.backgroundSize &&
              data.borderSize === emptyIconPluginData.borderSize,
          },
        ])}
        style={{
          '--alignment': data.alignment,
          '--spacing': data.spacing + 'px',
          '--sizing': data.sizing,
          '--color': data.color,
          '--backgroundSize': data.backgroundSize,
          '--backgroundRadius': data.backgroundRadius + '%',
          '--backgroundColor': data.backgroundColor,
          '--borderSize': data.borderSize + 'px',
          '--borderColor': data.borderColor,
        }}
      >
        {data.link ? (
          <a
            href={data.link}
            target={data.opentab ? '_blank' : undefined}
            rel={data.opentab ? 'noreferrer noopener' : undefined}
          >
            <span className={`ep-${data.type}-icon`} />
          </a>
        ) : (
          <span className={`ep-${data.type}-icon`} />
        )}
      </div>
    )
  }
}
